<template>
  <div>
    <DesktopFilters
      class="hidden md:mt-32 md:flex"
      :grades-options
      :initial-values="DEFAULT_VALUES"
      :loading-button
      :state-options
      @filter="handleFilter"
    />
    <MobileFilters
      class="md:hidden"
      :grades-options
      :initial-values="DEFAULT_VALUES"
      :loading-button
      :state-options
      @filter="handleFilter"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  LISTING_PUBLICATION_STATE,
  QUOTES_LISTING_GRADES,
} from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { VALUE_FILTER_ALL } from '~/constants/buyback'

import translations from './Filters.translations'
import type {
  FilterEventPayload,
  FilterOption,
  WishlistFilters,
} from './Filters.types'
import DesktopFilters from './components/DesktopFilters.vue'
import MobileFilters from './components/MobileFilters.vue'

const VALUE_WITH_BACKBOX = 'withBackbox'
const VALUE_WITHOUT_BACKBOX = 'withoutBackbox'
const DEFAULT_VALUES: WishlistFilters = {
  model: '',
  brand: '',
  grades: VALUE_FILTER_ALL,
  state: VALUE_FILTER_ALL,
  productId: '',
  sku: '',
  releaseYear: '',
}

withDefaults(
  defineProps<{
    loadingButton?: string
  }>(),
  {
    loadingButton: '',
  },
)

const emit = defineEmits<{
  (
    e: 'filter',
    payload: { filters: WishlistFilters; actionButton: string | undefined },
  ): void
}>()

const i18n = useI18n()

const gradesOptions = computed<Array<FilterOption>>(() => [
  { label: i18n(translations.gradeAll), value: VALUE_FILTER_ALL },
  {
    label: i18n(translations.gradeDiamond),
    value: QUOTES_LISTING_GRADES.FUNCTIONAL_FLAWLESS,
  },
  {
    label: i18n(translations.gradePlatinum),
    value: QUOTES_LISTING_GRADES.FUNCTIONAL_GOOD,
  },
  {
    label: i18n(translations.gradeGold),
    value: QUOTES_LISTING_GRADES.FUNCTIONAL_USED,
  },
  {
    label: i18n(translations.gradeSilver),
    value: QUOTES_LISTING_GRADES.FUNCTIONAL_CRACKED,
  },
  {
    label: i18n(translations.gradeBronze),
    value: QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_USED,
  },
  {
    label: i18n(translations.gradeStallone),
    value: QUOTES_LISTING_GRADES.NOT_FUNCTIONAL_CRACKED,
  },
])

const stateOptions = computed(() => [
  {
    label: i18n(translations.onlineAndOffline),
    value: VALUE_FILTER_ALL,
  },
  { label: i18n(translations.withBackbox), value: VALUE_WITH_BACKBOX },
  {
    label: i18n(translations.withoutBackbox),
    value: VALUE_WITHOUT_BACKBOX,
  },
  {
    label: i18n(translations.offline),
    value: LISTING_PUBLICATION_STATE.OFFLINE,
  },
  {
    label: i18n(translations.online),
    value: LISTING_PUBLICATION_STATE.ONLINE,
  },
])

function extractBackboxAndPublicationState(publicationState: string) {
  let payload = {}

  switch (publicationState) {
    case VALUE_FILTER_ALL:
      payload = {
        state: VALUE_FILTER_ALL,
        hasBackbox: '',
      }
      break

    case VALUE_WITH_BACKBOX:
      payload = {
        state: LISTING_PUBLICATION_STATE.ONLINE,
        hasBackbox: 'true',
      }
      break

    case VALUE_WITHOUT_BACKBOX:
      payload = {
        state: LISTING_PUBLICATION_STATE.ONLINE,
        hasBackbox: 'false',
      }
      break

    case LISTING_PUBLICATION_STATE.OFFLINE:
      payload = {
        state: LISTING_PUBLICATION_STATE.OFFLINE,
        hasBackbox: '',
      }
      break

    case LISTING_PUBLICATION_STATE.ONLINE:
      payload = {
        state: LISTING_PUBLICATION_STATE.ONLINE,
        hasBackbox: '',
      }
      break

    default:
      payload = {}
  }

  return payload
}

function handleFilter(args: FilterEventPayload) {
  const { filters, actionButton } = args
  const { state } = filters

  const backBoxAndState = extractBackboxAndPublicationState(String(state))

  emit('filter', {
    filters: { ...filters, ...backBoxAndState },
    actionButton,
  })
}
</script>
